import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './CalendarFeeds.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Settings -> Calendars (migration 077): the public event calendars everybody
 * may show as layers on their to-do Calendar - the MCC's programme, what is
 * on in Gozo, Malta's public holidays.
 *
 * Administrators keep the list: a name, an https iCalendar address, a
 * colour, a category, whether it is offered at all, and whether it is on for
 * people who have not chosen. Jarvis fetches each one every few hours (and on
 * "Refresh"), and this screen says how that went - when, how many events,
 * the next one, or the error in words - so a calendar that has stopped
 * working is seen here rather than by its absence.
 *
 * The built-in holidays are worked out by Jarvis: no address, nothing to
 * fetch, and they cannot be removed (only switched off).
 *
 * Every check the form makes the server makes too (and more - the address is
 * resolved and refused if it points inside a private network); the form's
 * are only there to say so sooner.
 */

const COLORS = ['red', 'orange', 'yellow', 'green', 'teal', 'blue', 'purple', 'pink', 'grey'];
const CATEGORY_LABELS = { work: 'Work / CPD', culture: 'Culture', gozo: 'Gozo', holidays: 'Holidays' };
const EMPTY = { name: '', url: '', color: 'blue', category: 'culture', enabled: true, default_on: false };

const cap = (c) => c[0].toUpperCase() + c.slice(1);

/** "3 minutes ago", "yesterday", "on 12 Oct". */
export function ago(iso, now = new Date()) {
  if (!iso) return '';
  const t = new Date(iso);
  const s = Math.round((now - t) / 1000);
  if (Number.isNaN(s)) return '';
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  if (d === 1) return 'yesterday';
  if (d < 7) return `${d} days ago`;
  return `on ${t.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
}

const when = (iso, allDay) => {
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return '';
  const day = t.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  return allDay ? day : `${day}, ${t.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

/** What the form can say before the server does. */
function checkForm(f, builtin) {
  if (!String(f.name || '').trim()) return { field: 'name', text: 'Give the calendar a name' };
  if (String(f.name).trim().length > 80) return { field: 'name', text: 'That name is too long - 80 characters at most' };
  if (!builtin) {
    const u = String(f.url || '').trim();
    if (!u) return { field: 'url', text: 'Give the calendar’s address (an https:// link to an .ics feed)' };
    if (!/^(https|webcals?):\/\//i.test(u)) return { field: 'url', text: 'Only https:// calendar addresses are allowed' };
  }
  return null;
}

function FeedForm({ initial, builtin = false, categories, saving, problem, onSave, onCancel, submitLabel }) {
  const [f, setF] = useState(initial);
  const [local, setLocal] = useState(null);
  const nameRef = useRef(null);
  useEffect(() => { if (nameRef.current) nameRef.current.focus(); }, []);
  const set = (k, v) => { setF((x) => ({ ...x, [k]: v })); setLocal(null); };
  const submit = (e) => {
    e.preventDefault();
    const bad = checkForm(f, builtin);
    if (bad) { setLocal(bad); return; }
    const body = { name: f.name.trim(), color: f.color, category: f.category, enabled: f.enabled, default_on: f.default_on };
    if (!builtin) body.url = f.url.trim();
    onSave(body);
  };
  const said = local || (problem ? { field: /address|https|private|redirect|port|already/i.test(problem) ? 'url' : null, text: problem } : null);
  const idp = `cfe-f-${initial.id || 'new'}`;
  return (
    <form className="cfe-form" onSubmit={submit} noValidate aria-label={submitLabel}>
      <div className="cfe-fields">
        <label className="cfe-field" htmlFor={`${idp}-name`}>
          <span>Name</span>
          <input id={`${idp}-name`} ref={nameRef} type="text" value={f.name} maxLength={80}
                 aria-invalid={said?.field === 'name' ? 'true' : undefined}
                 placeholder="e.g. Teatru Manoel" onChange={(e) => set('name', e.target.value)} />
        </label>
        {!builtin && (
          <label className="cfe-field cfe-field-url" htmlFor={`${idp}-url`}>
            <span>Address</span>
            <input id={`${idp}-url`} type="url" inputMode="url" value={f.url} spellCheck={false} autoComplete="off"
                   aria-invalid={said?.field === 'url' ? 'true' : undefined} aria-describedby={`${idp}-url-hint`}
                   placeholder="https://example.mt/events/?ical=1" onChange={(e) => set('url', e.target.value)} />
            <small id={`${idp}-url-hint`}>An iCalendar (.ics) feed, https only. Many event sites have one at <code>/events/?ical=1</code>.</small>
          </label>
        )}
        <label className="cfe-field" htmlFor={`${idp}-cat`}>
          <span>Category</span>
          <select id={`${idp}-cat`} value={f.category} onChange={(e) => set('category', e.target.value)}>
            {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </label>
        <fieldset className="cfe-field cfe-colors">
          <legend>Colour</legend>
          <div className="cfe-swatches">
            {COLORS.map((c) => (
              <label key={c} className={`cfe-swatch cfe-sw-${c}`} title={cap(c)}>
                <input type="radio" name={`${idp}-color`} value={c} checked={f.color === c} onChange={() => set('color', c)} />
                <span className="cfe-swatch-dot" aria-hidden="true" />
                <span className="cfe-sr">{cap(c)}</span>
              </label>
            ))}
          </div>
        </fieldset>
      </div>
      <div className="cfe-checks">
        <label className="cfe-check">
          <input type="checkbox" checked={f.enabled} onChange={(e) => set('enabled', e.target.checked)} />
          <span>Offered to everybody as a layer on their Calendar</span>
        </label>
        <label className="cfe-check">
          <input type="checkbox" checked={f.default_on} onChange={(e) => set('default_on', e.target.checked)} />
          <span>Switched on for people who have not chosen (they can still hide it)</span>
        </label>
      </div>
      {said && <p className="cfe-problem" role="alert">{said.text}</p>}
      <div className="cfe-form-actions">
        <button type="submit" className="btn btn-primary btn-small" disabled={saving}>{saving ? 'Saving…' : submitLabel}</button>
        <button type="button" className="btn btn-secondary btn-small" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </form>
  );
}

function StatusLine({ feed, busy }) {
  if (busy === 'refresh') return <span className="cfe-status is-busy">Fetching…</span>;
  if (feed.builtin) {
    return (
      <span className="cfe-status is-ok">
        Worked out by Jarvis (Public Holidays Act) - nothing to fetch.
        {feed.next_event && <> Next: <strong>{feed.next_event.title}</strong>, {when(feed.next_event.starts_at, true)}.</>}
      </span>
    );
  }
  if (!feed.last_fetched_at) return <span className="cfe-status is-never">Not fetched yet.</span>;
  if (feed.last_status === 'error') {
    return (
      <span className="cfe-status is-error">
        <strong>Last fetch failed</strong> {ago(feed.last_fetched_at)}: {feed.last_error || 'unknown error'}.
        {feed.last_ok_at && ` Still showing what was fetched ${ago(feed.last_ok_at)}.`}
      </span>
    );
  }
  const n = feed.stored ?? feed.last_count ?? 0;
  return (
    <span className="cfe-status is-ok">
      Fetched {ago(feed.last_fetched_at)} · {n} event{n === 1 ? '' : 's'}
      {feed.next_event && <> · next: <strong>{feed.next_event.title}</strong>, {when(feed.next_event.starts_at, feed.next_event.all_day)}</>}
      {feed.last_error && <> · {feed.last_error}</>}
    </span>
  );
}

export default function CalendarFeeds() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [problem, setProblem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState({});     // id -> 'refresh' | 'toggle' | 'remove'
  const [confirm, setConfirm] = useState(null);
  const [flash, setFlash] = useState(null);
  const addButton = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/calendar-feeds`);
      // The answer is { feeds, categories, refresh_hours }; anything else
      // (a proxy's empty page, an older server) reads as no calendars rather
      // than taking the whole Settings page down with it.
      const d = r.data && typeof r.data === 'object' && !Array.isArray(r.data) ? r.data : {};
      setData({ ...d, feeds: Array.isArray(d.feeds) ? d.feeds : [] });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the calendars');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const replace = (feed) => setData((d) => (d ? { ...d, feeds: d.feeds.map((x) => (x.id === feed.id ? feed : x)) } : d));
  const mark = (id, what) => setBusy((b) => { const n = { ...b }; if (what) n[id] = what; else delete n[id]; return n; });

  const refresh = async (feed) => {
    mark(feed.id, 'refresh');
    try {
      const r = await axios.post(`${API_BASE}/calendar-feeds/${feed.id}/refresh`, {});
      if (r.data?.feed) replace(r.data.feed);
      const res = r.data?.result;
      setFlash(res?.ok ? `“${feed.name}” refreshed${res.count !== null && res.count !== undefined ? ` - ${res.count} event${res.count === 1 ? '' : 's'}` : ''}.`
        : `“${feed.name}” could not be fetched: ${res?.error || 'unknown error'}.`);
    } catch (err) {
      setFlash(err.response?.data?.error || `Could not refresh “${feed.name}”`);
    } finally { mark(feed.id, null); }
  };

  const refreshAll = async () => {
    mark('all', 'refresh');
    try {
      const r = await axios.post(`${API_BASE}/calendar-feeds/refresh`, {});
      if (r.data?.feeds) setData((d) => ({ ...d, feeds: r.data.feeds }));
      const did = r.data?.refreshed || [];
      const bad = did.filter((x) => !x.ok).length;
      setFlash(`${did.length} calendar${did.length === 1 ? '' : 's'} refreshed${bad ? `, ${bad} failed - see below` : ''}.`);
    } catch (err) {
      setFlash(err.response?.data?.error || 'Could not refresh the calendars');
    } finally { mark('all', null); }
  };

  const create = async (body) => {
    setSaving(true);
    setProblem(null);
    try {
      const r = await axios.post(`${API_BASE}/calendar-feeds`, body);
      setData((d) => ({ ...d, feeds: [...d.feeds, r.data] }));
      setAdding(false);
      setFlash(`“${r.data.name}” added. Fetching it now…`);
      // The first fetch straight away, so the screen says whether it works.
      await refresh(r.data);
    } catch (err) {
      setProblem(err.response?.data?.error || 'Could not add the calendar');
    } finally { setSaving(false); }
  };

  const save = async (feed, body) => {
    setSaving(true);
    setProblem(null);
    try {
      const changes = {};
      for (const [k, v] of Object.entries(body)) if (feed[k] !== v) changes[k] = v;
      if (!Object.keys(changes).length) { setEditing(null); return; }
      const r = await axios.patch(`${API_BASE}/calendar-feeds/${feed.id}`, changes);
      replace(r.data);
      setEditing(null);
      setFlash(`“${r.data.name}” saved.`);
      if (changes.url) await refresh(r.data);
    } catch (err) {
      setProblem(err.response?.data?.error || 'Could not save the calendar');
    } finally { setSaving(false); }
  };

  const toggle = async (feed) => {
    mark(feed.id, 'toggle');
    try {
      const r = await axios.patch(`${API_BASE}/calendar-feeds/${feed.id}`, { enabled: !feed.enabled });
      replace(r.data);
      setFlash(`“${feed.name}” is ${r.data.enabled ? 'offered to everybody again' : 'no longer offered'}.`);
    } catch (err) {
      setFlash(err.response?.data?.error || 'Could not change that');
    } finally { mark(feed.id, null); }
  };

  const remove = async (feed) => {
    mark(feed.id, 'remove');
    try {
      await axios.delete(`${API_BASE}/calendar-feeds/${feed.id}`);
      setData((d) => ({ ...d, feeds: d.feeds.filter((x) => x.id !== feed.id) }));
      setConfirm(null);
      setFlash(`“${feed.name}” removed.`);
      if (addButton.current) addButton.current.focus();
    } catch (err) {
      setFlash(err.response?.data?.error || 'Could not remove the calendar');
    } finally { mark(feed.id, null); }
  };

  if (error && !data) {
    const migration = /migrate:\d+/.exec(error);
    return (
      <div className="cfe">
        <h2 className="cfe-title">Calendars</h2>
        <p className={migration ? 'cfe-notready' : 'cfe-problem'} role="alert">{error}</p>
      </div>
    );
  }
  if (!data) return <div className="cfe"><p className="cfe-quiet">Loading…</p></div>;

  const categories = data.categories || Object.entries(CATEGORY_LABELS).map(([key, label]) => ({ key, label }));
  const label = (k) => (categories.find((c) => c.key === k) || {}).label || k;

  return (
    <div className="cfe" id="s-feeds">
      <div className="cfe-top">
        <div>
          <h2 className="cfe-title">Calendars</h2>
          <p className="cfe-intro">
            Public event calendars everybody can switch on as layers in their to-do Calendar. They are read-only there,
            each in its own colour; Jarvis fetches them every {data.refresh_hours || 6} hours. Only the holidays are on
            unless somebody chooses otherwise.
          </p>
        </div>
        <div className="cfe-top-actions">
          <button type="button" className="btn btn-secondary btn-small" onClick={refreshAll} disabled={Boolean(busy.all)}>
            {busy.all ? 'Refreshing…' : 'Refresh all'}
          </button>
          <button type="button" ref={addButton} className="btn btn-primary btn-small" disabled={adding}
                  onClick={() => { setAdding(true); setEditing(null); setProblem(null); }}>
            Add a calendar
          </button>
        </div>
      </div>

      <p className="cfe-flash" role="status" aria-live="polite">{flash || ''}</p>

      {adding && (
        <div className="cfe-card is-form">
          <h3 className="cfe-sub">Add a calendar</h3>
          <FeedForm initial={EMPTY} categories={categories} saving={saving} problem={problem} submitLabel="Add calendar"
                    onSave={create} onCancel={() => { setAdding(false); setProblem(null); }} />
        </div>
      )}

      {data.feeds.length === 0 && !adding && <p className="cfe-quiet">No calendars yet.</p>}
      <ul className="cfe-list" aria-label="Calendars">
        {data.feeds.map((feed) => (
          <li key={feed.id} className={`cfe-card${feed.enabled ? '' : ' is-off'}`} data-cfe-feed={feed.id}>
            {editing === feed.id ? (
              <>
                <h3 className="cfe-sub">Change “{feed.name}”</h3>
                <FeedForm initial={{ ...EMPTY, ...feed, url: feed.url || '' }} builtin={Boolean(feed.builtin)} categories={categories}
                          saving={saving} problem={problem} submitLabel="Save"
                          onSave={(body) => save(feed, body)} onCancel={() => { setEditing(null); setProblem(null); }} />
              </>
            ) : (
              <div className="cfe-row">
                <span className={`cfe-dot cfe-sw-${feed.color}`} aria-hidden="true" />
                <div className="cfe-main">
                  <div className="cfe-name">
                    <span className="cfe-name-text">{feed.name}</span>
                    <span className="cfe-tag">{label(feed.category)}</span>
                    {feed.builtin && <span className="cfe-tag is-builtin">Built in</span>}
                    {feed.default_on && <span className="cfe-tag is-on">On by default</span>}
                    {!feed.enabled && <span className="cfe-tag is-off">Not offered</span>}
                  </div>
                  {feed.url && <div className="cfe-url" title={feed.url}>{feed.url}</div>}
                  <div className="cfe-state"><StatusLine feed={feed} busy={busy[feed.id]} /></div>
                </div>
                <div className="cfe-actions">
                  <label className="cfe-switch">
                    <input type="checkbox" role="switch" checked={feed.enabled} disabled={Boolean(busy[feed.id])}
                           aria-label={`Offer “${feed.name}” to everybody`} onChange={() => toggle(feed)} />
                    <span>Offered</span>
                  </label>
                  {!feed.builtin && (
                    <button type="button" className="btn btn-secondary btn-small" disabled={Boolean(busy[feed.id])}
                            aria-label={`Refresh “${feed.name}” now`} onClick={() => refresh(feed)}>Refresh</button>
                  )}
                  <button type="button" className="btn btn-secondary btn-small" aria-label={`Change “${feed.name}”`}
                          onClick={() => { setEditing(feed.id); setAdding(false); setProblem(null); setConfirm(null); }}>Edit</button>
                  {!feed.builtin && (confirm === feed.id ? (
                    <span className="cfe-confirm" role="group" aria-label={`Remove “${feed.name}”?`}>
                      <span>Remove it for everybody?</span>
                      <button type="button" className="btn btn-danger btn-small" disabled={Boolean(busy[feed.id])}
                              onClick={() => remove(feed)}>Remove</button>
                      <button type="button" className="btn btn-secondary btn-small" onClick={() => setConfirm(null)}>Keep</button>
                    </span>
                  ) : (
                    <button type="button" className="btn btn-secondary btn-small cfe-remove" aria-label={`Remove “${feed.name}”`}
                            onClick={() => setConfirm(feed.id)}>Remove</button>
                  ))}
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
      <p className="cfe-foot">
        Jarvis only fetches https addresses on the public internet, follows at most three redirects, gives up after
        15 seconds and reads at most 5 MB. Event text is shown as plain text; links open in a new tab.
      </p>
    </div>
  );
}
